const LIMITS = { source: 2000, tokens: 200, depth: 20, operations: 80 }
const PRECEDENCE = { or: 1, and: 2, '==': 3, '!=': 3, '>': 4, '>=': 4, '<': 4, '<=': 4, '+': 5, '-': 5, '*': 6, '/': 6, '%': 6, '^': 7 }
const FUNCTIONS = ['abs', 'round', 'concat', 'upper', 'lower', 'date_add', 'days_between', 'if']

class ExpressionError extends Error {}

class Lexer {
  constructor (source) {
    this.source = String(source)
    this.index = 0
    this.tokens = []
    if (this.source.length > LIMITS.source) throw new ExpressionError('expression is too long')
  }

  call () {
    while (this.index < this.source.length) {
      this.skipWhitespace()
      if (this.index >= this.source.length) break
      const token = this.field() || this.string() || this.number() || this.word() || this.operator()
      if (!token) throw new ExpressionError('invalid token')
      this.tokens.push(token)
      if (this.tokens.length > LIMITS.tokens) throw new ExpressionError('expression has too many tokens')
    }
    this.tokens.push({ type: 'eof' })
    return this.tokens
  }

  field () {
    if (!this.rest().startsWith('{{')) return null
    const closing = this.source.indexOf('}}', this.index + 2)
    if (closing < 0) throw new ExpressionError('unterminated field reference')
    const value = this.source.slice(this.index + 2, closing).trim()
    if (!value) throw new ExpressionError('empty field reference')
    this.index = closing + 2
    return { type: 'field', value }
  }

  string () {
    const quote = this.source[this.index]
    if (quote !== "'" && quote !== '"') return null
    this.index += 1
    let value = ''
    while (this.index < this.source.length) {
      const char = this.source[this.index++]
      if (char === quote) return { type: 'string', value }
      if (char === '\\') {
        if (this.index >= this.source.length) throw new ExpressionError('unterminated escape')
        const escaped = this.source[this.index++]
        value += ({ n: '\n', t: '\t', '\\': '\\', [quote]: quote })[escaped] ?? escaped
      } else {
        value += char
      }
    }
    throw new ExpressionError('unterminated string')
  }

  number () {
    const match = /^(?:\d+(?:\.\d+)?|\.\d+)/.exec(this.rest())
    if (!match) return null
    this.index += match[0].length
    return { type: 'number', value: Number(match[0]) }
  }

  word () {
    const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(this.rest())
    if (!match) return null
    this.index += match[0].length
    const value = match[0].toLowerCase()
    if (value === 'true') return { type: 'literal', value: true }
    if (value === 'false') return { type: 'literal', value: false }
    if (value === 'null') return { type: 'literal', value: null }
    if (['and', 'or', 'not'].includes(value)) return { type: 'operator', value }
    return { type: 'identifier', value }
  }

  operator () {
    const value = ['>=', '<=', '==', '!=', '+', '-', '*', '/', '%', '^', '>', '<', '(', ')', ','].find((candidate) => this.rest().startsWith(candidate))
    if (!value) return null
    this.index += value.length
    return { type: ['(', ')', ','].includes(value) ? value : 'operator', value }
  }

  rest () { return this.source.slice(this.index) }
  skipWhitespace () { while (/\s/.test(this.source[this.index] || '')) this.index += 1 }
}

class Parser {
  constructor (source) {
    this.tokens = new Lexer(source).call()
    this.index = 0
  }

  call () {
    const ast = this.expression()
    if (this.current().type !== 'eof') throw new ExpressionError('unexpected token')
    return ast
  }

  expression (minimum = 0, depth = 0) {
    if (depth > LIMITS.depth) throw new ExpressionError('expression is too deeply nested')
    let left = this.prefix(depth)
    while (this.current().type === 'operator' && PRECEDENCE[this.current().value] >= minimum) {
      const operator = this.advance().value
      const right = this.expression(PRECEDENCE[operator] + 1, depth + 1)
      left = { type: 'binary', operator, left, right }
    }
    return left
  }

  prefix (depth) {
    const token = this.advance()
    if (['number', 'string', 'literal'].includes(token.type)) return { type: 'literal', value: token.value }
    if (token.type === 'field') return { type: 'field', uuid: token.value }
    if (token.type === 'operator' && ['-', 'not'].includes(token.value)) {
      return { type: 'unary', operator: token.value, value: this.expression(7, depth + 1) }
    }
    if (token.type === 'identifier') return this.parseCall(token.value, depth)
    if (token.type === '(') {
      const value = this.expression(0, depth + 1)
      this.expect(')')
      return value
    }
    throw new ExpressionError('expected a value')
  }

  parseCall (name, depth) {
    this.expect('(')
    const args = []
    if (this.current().type !== ')') {
      while (true) {
        args.push(this.expression(0, depth + 1))
        if (this.current().type !== ',') break
        this.advance()
      }
    }
    this.expect(')')
    return { type: 'call', name, arguments: args }
  }

  current () { return this.tokens[this.index] }
  advance () { return this.tokens[this.index++] }
  expect (type) {
    if (this.current().type !== type) throw new ExpressionError('unexpected token')
    return this.advance()
  }
}

class Evaluator {
  constructor (values = {}) {
    this.values = values
    this.operations = 0
  }

  call (ast, depth = 0) {
    if (depth > LIMITS.depth) throw new ExpressionError('expression is too deeply nested')
    if (++this.operations > LIMITS.operations) throw new ExpressionError('expression has too many operations')
    if (ast.type === 'literal') return ast.value
    if (ast.type === 'field') return this.values[ast.uuid]
    if (ast.type === 'unary') {
      const value = this.call(ast.value, depth + 1)
      return ast.operator === 'not' ? !truthy(value) : boundedNumber(-numeric(value))
    }
    if (ast.type === 'binary') return this.binary(ast, depth)
    if (ast.type === 'call') return this.functionCall(ast, depth)
    throw new ExpressionError('unknown expression node')
  }

  binary (ast, depth) {
    const left = this.call(ast.left, depth + 1)
    if (ast.operator === 'and') return truthy(left) && truthy(this.call(ast.right, depth + 1))
    if (ast.operator === 'or') return truthy(left) || truthy(this.call(ast.right, depth + 1))
    const right = this.call(ast.right, depth + 1)
    if (ast.operator === '+') return boundedNumber(numeric(left) + numeric(right))
    if (ast.operator === '-') return boundedNumber(numeric(left) - numeric(right))
    if (ast.operator === '*') return boundedNumber(numeric(left) * numeric(right))
    if (ast.operator === '/') {
      const divisor = numeric(right)
      if (divisor === 0) throw new ExpressionError('division by zero')
      return boundedNumber(numeric(left) / divisor)
    }
    if (ast.operator === '%') {
      const divisor = numeric(right)
      if (divisor === 0) throw new ExpressionError('division by zero')
      return boundedNumber(numeric(left) % divisor)
    }
    if (ast.operator === '^') {
      const exponent = numeric(right)
      if (Math.abs(exponent) > 100) throw new ExpressionError('exponent is outside the supported range')
      return boundedNumber(numeric(left) ** exponent)
    }
    if (ast.operator === '==') return JSON.stringify(left) === JSON.stringify(right)
    if (ast.operator === '!=') return JSON.stringify(left) !== JSON.stringify(right)
    const comparison = compare(left, right)
    if (ast.operator === '>') return comparison > 0
    if (ast.operator === '>=') return comparison >= 0
    if (ast.operator === '<') return comparison < 0
    if (ast.operator === '<=') return comparison <= 0
    throw new ExpressionError('unknown operator')
  }

  functionCall (ast, depth) {
    if (!FUNCTIONS.includes(ast.name)) throw new ExpressionError('unknown function')
    if (ast.name === 'if') {
      arity(ast.arguments, 3)
      return this.call(truthy(this.call(ast.arguments[0], depth + 1)) ? ast.arguments[1] : ast.arguments[2], depth + 1)
    }
    const args = ast.arguments.map((argument) => this.call(argument, depth + 1))
    if (ast.name === 'abs') { arity(args, 1); return boundedNumber(Math.abs(numeric(args[0]))) }
    if (ast.name === 'round') {
      arity(args, [1, 2])
      const precision = args[1] == null ? 0 : boundedInteger(args[1], 12)
      const factor = 10 ** precision
      const scaled = numeric(args[0]) * factor
      return boundedNumber(Math.sign(scaled) * Math.round(Math.abs(scaled) + Number.EPSILON) / factor)
    }
    if (ast.name === 'concat') return args.map(stringify).join('')
    if (ast.name === 'upper') { arity(args, 1); return stringify(args[0]).toUpperCase() }
    if (ast.name === 'lower') { arity(args, 1); return stringify(args[0]).toLowerCase() }
    if (ast.name === 'date_add') {
      arity(args, 2)
      if (args[0] == null || args[0] === '') return null
      const date = dateValue(args[0])
      date.setUTCDate(date.getUTCDate() + boundedInteger(args[1], 365000))
      return date.toISOString().slice(0, 10)
    }
    if (ast.name === 'days_between') {
      arity(args, 2)
      if (args[0] == null || args[0] === '' || args[1] == null || args[1] === '') return null
      return Math.round((dateValue(args[1]) - dateValue(args[0])) / 86400000)
    }
  }
}

class TypeChecker {
  constructor (fieldsIndex) { this.fieldsIndex = fieldsIndex }

  call (ast) {
    if (ast.type === 'interpolation') return 'string'
    if (ast.type === 'literal') {
      if (ast.value == null) return 'null'
      if (typeof ast.value === 'boolean') return 'boolean'
      if (typeof ast.value === 'number') return 'number'
      return /^\d{4}-\d{2}-\d{2}$/.test(ast.value) ? 'date' : 'string'
    }
    if (ast.type === 'field') return ({ number: 'number', date: 'date', checkbox: 'boolean' })[this.fieldsIndex[ast.uuid].type] || 'string'
    if (ast.type === 'unary') {
      const expected = ast.operator === 'not' ? 'boolean' : 'number'
      this.expect(ast.value, expected)
      return expected
    }
    if (ast.type === 'binary') return this.binary(ast)
    if (ast.type === 'call') return this.functionCall(ast)
    throw new ExpressionError('unknown expression type')
  }

  expect (ast, expected) {
    const actual = typeof ast === 'string' ? ast : this.call(ast)
    if (actual !== expected && actual !== 'null') throw new ExpressionError(`expected ${expected}`)
  }

  compatible (left, right) {
    if (left !== right && left !== 'null' && right !== 'null') throw new ExpressionError('incompatible expression types')
  }

  binary (ast) {
    const left = this.call(ast.left)
    const right = this.call(ast.right)
    if (['+', '-', '*', '/', '%', '^'].includes(ast.operator)) {
      this.expect(left, 'number'); this.expect(right, 'number'); return 'number'
    }
    if (['and', 'or'].includes(ast.operator)) {
      this.expect(left, 'boolean'); this.expect(right, 'boolean'); return 'boolean'
    }
    this.compatible(left, right)
    return 'boolean'
  }

  functionCall (ast) {
    const types = ast.arguments.map((argument) => this.call(argument))
    if (ast.name === 'abs') { arity(types, 1); this.expect(types[0], 'number'); return 'number' }
    if (ast.name === 'round') {
      arity(types, [1, 2]); this.expect(types[0], 'number'); if (types[1]) this.expect(types[1], 'number'); return 'number'
    }
    if (ast.name === 'concat') return 'string'
    if (['upper', 'lower'].includes(ast.name)) { arity(types, 1); this.expect(types[0], 'string'); return 'string' }
    if (ast.name === 'date_add') {
      arity(types, 2); this.expect(types[0], 'date'); this.expect(types[1], 'number'); return 'date'
    }
    if (ast.name === 'days_between') {
      arity(types, 2); this.expect(types[0], 'date'); this.expect(types[1], 'date'); return 'number'
    }
    if (ast.name === 'if') {
      arity(types, 3); this.expect(types[0], 'boolean'); this.compatible(types[1], types[2]); return types[1]
    }
    throw new ExpressionError('unknown function')
  }
}

class TemplateEvaluator {
  constructor (fields, values = {}) {
    this.fields = fields || []
    this.values = values || {}
    this.fieldsIndex = Object.fromEntries(this.fields.map((field) => [field.uuid, field]))
    this.astCache = new Map()
    this.computed = {}
    this.computedReady = false
    this.validate()
  }

  validate () {
    const graph = {}
    const checker = new TypeChecker(this.fieldsIndex)
    for (const field of this.fields) {
      const sources = []
      const formula = field.preferences?.formula
      const formulaAst = formula ? this.parseFormula(formula, field) : null
      if (formulaAst) sources.push(formulaAst)
      for (const key of ['visible', 'required', 'readonly']) {
        if (field.conditional_rules?.[key]) {
          const ruleAst = this.parse(field.conditional_rules[key])
          sources.push(ruleAst)
        }
      }
      graph[field.uuid] = [...new Set(sources.flatMap(references))]
      if (graph[field.uuid].some((uuid) => !this.fieldsIndex[uuid])) throw new ExpressionError('expression references an unknown field')

      if (formulaAst) {
        const expected = ({ number: 'number', date: 'date', checkbox: 'boolean' })[field.type] || 'string'
        const actual = checker.call(formulaAst)
        if (actual !== expected && !(expected === 'string' && ['number', 'date', 'boolean'].includes(actual))) {
          throw new ExpressionError('formula result does not match the field type')
        }
      }
      for (const key of ['visible', 'required', 'readonly']) {
        if (field.conditional_rules?.[key]) checker.expect(this.parse(field.conditional_rules[key]), 'boolean')
      }
    }
    detectCycles(graph)
    return this
  }

  computedValues () {
    if (this.computedReady) return this.computed
    for (const field of this.fields) {
      if (field.preferences?.formula) this.formulaValue(field, [])
    }
    this.computedReady = true
    return this.computed
  }

  formulaValue (field, stack) {
    if (Object.hasOwn(this.computed, field.uuid)) return this.computed[field.uuid]
    if (stack.includes(field.uuid)) throw new ExpressionError('expression dependency cycle')
    const source = field.preferences.formula
    const ast = this.parseFormula(source, field)
    for (const uuid of references(ast)) {
      const dependency = this.fieldsIndex[uuid]
      if (dependency?.preferences?.formula) this.formulaValue(dependency, stack.concat(field.uuid))
    }
    const values = { ...this.values, ...this.computed }
    const value = ast.type === 'interpolation'
      ? source.replace(/\{\{(.*?)\}\}/g, (_, uuid) => stringify(values[uuid.trim()]))
      : new Evaluator(values).call(ast)
    this.computed[field.uuid] = field.type === 'text' ? stringify(value) : value
    return this.computed[field.uuid]
  }

  evaluate (source) {
    const ast = this.parse(source)
    const formulas = {}
    for (const uuid of references(ast)) {
      const field = this.fieldsIndex[uuid]
      if (field?.preferences?.formula) formulas[uuid] = this.formulaValue(field, [])
    }
    return new Evaluator({ ...this.values, ...formulas }).call(ast)
  }

  visible (field) { return !field.conditional_rules?.visible || truthy(this.evaluate(field.conditional_rules.visible)) }
  required (field) { return field.conditional_rules?.required ? truthy(this.evaluate(field.conditional_rules.required)) : field.required === true }
  readonly (field) { return field.readonly === true || (field.conditional_rules?.readonly && truthy(this.evaluate(field.conditional_rules.readonly))) }

  parse (source) {
    if (!this.astCache.has(source)) this.astCache.set(source, new Parser(source).call())
    return this.astCache.get(source)
  }

  parseFormula (source, field) {
    if (field.type !== 'text') return this.parse(source)
    try {
      return this.parse(source)
    } catch (error) {
      return { type: 'interpolation', references: [...source.matchAll(/\{\{(.*?)\}\}/g)].map((match) => match[1].trim()) }
    }
  }
}

function references (ast) {
  if (ast.type === 'field') return [ast.uuid]
  if (ast.type === 'interpolation') return ast.references
  return Object.values(ast).flatMap((value) => {
    if (Array.isArray(value)) return value.flatMap((item) => item && typeof item === 'object' ? references(item) : [])
    return value && typeof value === 'object' ? references(value) : []
  })
}

function detectCycles (graph) {
  const visiting = new Set()
  const visited = new Set()
  const visit = (uuid) => {
    if (visiting.has(uuid)) throw new ExpressionError('expression dependency cycle')
    if (visited.has(uuid)) return
    visiting.add(uuid)
    for (const dependency of graph[uuid] || []) if (graph[dependency]) visit(dependency)
    visiting.delete(uuid)
    visited.add(uuid)
  }
  Object.keys(graph).forEach(visit)
}

function numeric (value) {
  if (value == null || value === '') return 0
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && /^-?(?:\d+(?:\.\d+)?|\.\d+)$/.test(value)) return Number(value)
  throw new ExpressionError('expected a number')
}

function boundedNumber (value) {
  if (!Number.isFinite(value) || Math.abs(value) > 1e15) throw new ExpressionError('numeric result is outside the supported range')
  return value
}

function boundedInteger (value, maximum) {
  const number = numeric(value)
  if (!Number.isInteger(number) || Math.abs(number) > maximum) throw new ExpressionError('integer argument is outside the supported range')
  return number
}

function dateValue (value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value))) throw new ExpressionError('expected an ISO date')
  const date = new Date(`${value}T00:00:00.000Z`)
  if (Number.isNaN(date.valueOf()) || date.toISOString().slice(0, 10) !== value) throw new ExpressionError('expected an ISO date')
  return date
}

function compare (left, right) {
  if ((typeof left === 'number' || /^-?(?:\d+(?:\.\d+)?|\.\d+)$/.test(String(left))) &&
      (typeof right === 'number' || /^-?(?:\d+(?:\.\d+)?|\.\d+)$/.test(String(right)))) return Math.sign(numeric(left) - numeric(right))
  const leftString = stringify(left)
  const rightString = stringify(right)
  return leftString === rightString ? 0 : (leftString > rightString ? 1 : -1)
}

function stringify (value) { return Array.isArray(value) ? value.join(', ') : (value == null ? '' : String(value)) }
function truthy (value) { return value !== false && value != null && value !== '' && (!Array.isArray(value) || value.length > 0) }
function arity (args, expected) {
  if (Array.isArray(expected) ? expected.includes(args.length) : args.length === expected) return
  throw new ExpressionError('invalid number of function arguments')
}

module.exports = { ExpressionError, Parser, Evaluator, TemplateEvaluator }
